import { useState, useEffect, useCallback } from 'react';
import {
  Settings2, Save, RotateCcw, Info,
  MapPin, Phone, Building2, Percent, Hash, CreditCard, AlertCircle, CheckCircle2, Users, Search,
} from 'lucide-react';
import { visitsApi } from '../../api/visits.js';
import { usersApi } from '../../api/users.js';

// ─── Group definitions ────────────────────────────────────────────────────────
const GROUPS = {
  Sales: {
    label: 'Sales Settings',
    description: 'Daily assignment caps for sales visit queues.',
    Icon: MapPin,
    color: '#1d4ed8',
    bg: '#eff6ff',
    border: '#bfdbfe',
    badgeBg: '#dbeafe',
    badgeColor: '#1e40af',
  },
  CRM: {
    label: 'CRM Settings',
    description: 'Daily assignment caps for CRM call queues.',
    Icon: Phone,
    color: '#15803d',
    bg: '#f0fdf4',
    border: '#bbf7d0',
    badgeBg: '#dcfce7',
    badgeColor: '#14532d',
  },
  Accounts: {
    label: 'Accounts & Tax',
    description: 'Company registration details and default GST / TDS / TCS rates.',
    Icon: Building2,
    color: '#7c3aed',
    bg: '#f5f3ff',
    border: '#ddd6fe',
    badgeBg: '#ede9fe',
    badgeColor: '#5b21b6',
  },
};

const TAB_ORDER = ['Sales', 'CRM', 'Accounts'];

// ─── Per-key metadata ─────────────────────────────────────────────────────────
const KEY_META = {
  max_visits_per_sales_user: { Icon: MapPin,    group: 'Sales' },
  max_queue_per_crm_user:    { Icon: Phone,     group: 'CRM' },
  crm_queue_distribution_user_ids: { Icon: Users, group: 'CRM' },
  company_gstin:   { Icon: Hash,       group: 'Accounts', inputType: 'text',    maxLen: 15, mono: true, upper: true, placeholder: '22AAAAA0000A1Z5' },
  company_pan:     { Icon: CreditCard, group: 'Accounts', inputType: 'text',    maxLen: 10, mono: true, upper: true, placeholder: 'AAAAA0000A' },
  default_tds_rate:{ Icon: Percent,    group: 'Accounts', inputType: 'percent' },
  default_tcs_rate:{ Icon: Percent,    group: 'Accounts', inputType: 'percent' },
  cgst_rate:       { Icon: Percent,    group: 'Accounts', inputType: 'percent' },
  sgst_rate:       { Icon: Percent,    group: 'Accounts', inputType: 'percent' },
  igst_rate:       { Icon: Percent,    group: 'Accounts', inputType: 'percent' },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
function getGroup(key) {
  return KEY_META[key]?.group ?? 'General';
}

function validate(setting, draft) {
  const dt = setting.dataType;
  if (dt === 'integer') {
    const n = parseInt(draft, 10);
    if (isNaN(n) || n < 1) return 'Must be a positive integer.';
  } else if (dt === 'decimal') {
    const n = parseFloat(draft);
    if (isNaN(n) || n < 0 || n > 100) return 'Must be between 0.00 and 100.00.';
  } else if (dt === 'user_list') {
    try {
      const arr = JSON.parse(draft);
      if (!Array.isArray(arr)) return 'Invalid user selection.';
    } catch {
      return 'Invalid user selection.';
    }
  } else {
    if (!draft.trim()) return 'Value cannot be empty.';
    const km = KEY_META[setting.key];
    if (km?.maxLen && draft.trim().length > km.maxLen)
      return `Maximum ${km.maxLen} characters.`;
  }
  return '';
}

// ─── SettingCard ──────────────────────────────────────────────────────────────
function SettingCard({ setting, onSaved }) {
  const km   = KEY_META[setting.key] ?? {};
  const grp  = GROUPS[km.group ?? 'Sales'] ?? GROUPS.Sales;
  const Icon = km.Icon ?? Settings2;

  const [draft,  setDraft]  = useState(setting.value);
  const [saving, setSaving] = useState(false);
  const [saved,  setSaved]  = useState(false);
  const [error,  setError]  = useState('');

  const [crmUsers, setCrmUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userSearch, setUserSearch] = useState('');

  useEffect(() => {
    if (setting.dataType !== 'user_list') return;
    setLoadingUsers(true);
    usersApi.getAll({ role: 'crm,crm_manager', limit: 500 })
      .then(res => setCrmUsers(res?.data || []))
      .catch(() => {})
      .finally(() => setLoadingUsers(false));
  }, [setting.dataType]);

  const isDirty = draft !== setting.value;

  const handleChange = (raw) => {
    const v = km.upper ? raw.toUpperCase() : raw;
    setDraft(v);
    setError('');
    setSaved(false);
  };

  const handleSave = async () => {
    const err = validate(setting, draft);
    if (err) { setError(err); return; }
    setSaving(true); setError('');
    try {
      await visitsApi.updateSystemSetting(setting.key, draft.trim());
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      onSaved(setting.key, setting.dataType === 'decimal' ? parseFloat(draft).toFixed(2) : draft.trim());
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => { setDraft(setting.value); setError(''); setSaved(false); };

  // Input rendering
  const inputBase = {
    width: '100%',
    padding: setting.dataType === 'decimal' ? '9px 40px 9px 12px' : '9px 12px',
    border: `1.5px solid ${error ? '#fca5a5' : isDirty ? grp.border : '#e5e7eb'}`,
    borderRadius: 8,
    fontSize: '0.9rem',
    fontFamily: km.mono ? 'monospace' : 'inherit',
    background: '#fff',
    color: '#111827',
    outline: 'none',
    boxSizing: 'border-box',
    letterSpacing: km.mono ? '0.08em' : 'normal',
    transition: 'border-color 0.15s',
  };

  const renderInput = () => {
    if (setting.dataType === 'integer') {
      return (
        <input type="number" min="1" step="1" style={inputBase}
          value={draft} onChange={e => handleChange(e.target.value)} />
      );
    }
    if (setting.dataType === 'decimal') {
      return (
        <div style={{ position: 'relative' }}>
          <input type="number" min="0" max="100" step="0.01" style={inputBase}
            value={draft} onChange={e => handleChange(e.target.value)} />
          <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: '0.85rem', fontWeight: 600, pointerEvents: 'none' }}>%</span>
        </div>
      );
    }
    if (setting.dataType === 'user_list') {
      let selectedIds = [];
      try { selectedIds = JSON.parse(draft || '[]'); } catch { /* ignore */ }
      const filteredUsers = crmUsers.filter(u => (u.name || '').toLowerCase().includes(userSearch.toLowerCase()));
      const toggleUser = (id) => {
        const next = selectedIds.includes(id) ? selectedIds.filter(x => x !== id) : [...selectedIds, id];
        handleChange(JSON.stringify(next));
      };
      return (
        <div>
          <div style={{ position: 'relative', marginBottom: 8 }}>
            <Search size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', pointerEvents: 'none' }} />
            <input style={{ ...inputBase, paddingLeft: 32 }} placeholder="Search CRM users…"
              value={userSearch} onChange={e => setUserSearch(e.target.value)} />
          </div>
          {selectedIds.length === 0 ? (
            <div style={{ fontSize: '0.78rem', color: '#15803d', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '8px 12px', marginBottom: 8 }}>
              No users selected — leads distribute to <strong>all active CRM users</strong> (default).
            </div>
          ) : (
            <div style={{ fontSize: '0.78rem', color: '#4338ca', background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 8, padding: '8px 12px', marginBottom: 8 }}>
              Leads will only distribute to the <strong>{selectedIds.length}</strong> selected user{selectedIds.length !== 1 ? 's' : ''} below.
            </div>
          )}
          <div style={{ maxHeight: 220, overflowY: 'auto', border: '1.5px solid #e5e7eb', borderRadius: 8 }}>
            {loadingUsers ? (
              <div style={{ padding: 16, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>Loading CRM users…</div>
            ) : filteredUsers.length === 0 ? (
              <div style={{ padding: 16, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>No CRM users found.</div>
            ) : filteredUsers.map(u => {
              const isSel = selectedIds.includes(u.id);
              return (
                <div key={u.id} onClick={() => toggleUser(u.id)}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', background: isSel ? '#eef2ff' : 'transparent' }}>
                  <input type="checkbox" checked={isSel} onChange={() => {}} style={{ width: 14, height: 14, accentColor: '#6366f1' }} />
                  <span style={{ fontSize: '0.85rem', fontWeight: isSel ? 600 : 500, color: isSel ? '#4f46e5' : '#111827' }}>{u.name}</span>
                  <span style={{ marginLeft: 'auto', fontSize: '0.7rem', color: '#9ca3af', textTransform: 'uppercase' }}>{u.role?.name}</span>
                </div>
              );
            })}
          </div>
        </div>
      );
    }
    return (
      <input type="text" style={inputBase} maxLength={km.maxLen}
        placeholder={km.placeholder ?? ''}
        value={draft} onChange={e => handleChange(e.target.value)} />
    );
  };

  return (
    <div style={{
      background: '#fff',
      border: `1.5px solid ${isDirty ? grp.border : '#e5e7eb'}`,
      borderRadius: 12,
      overflow: 'hidden',
      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
      transition: 'border-color 0.2s, box-shadow 0.2s',
    }}>
      {/* Header */}
      <div style={{
        background: grp.bg,
        borderBottom: `1px solid ${grp.border}`,
        padding: '14px 18px',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
      }}>
        <div style={{
          width: 34, height: 34, borderRadius: 8,
          background: '#fff',
          border: `1px solid ${grp.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <Icon size={16} color={grp.color} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#111827', lineHeight: 1.3 }}>
            {setting.label}
          </div>
          <span style={{
            display: 'inline-block', marginTop: 3,
            fontSize: '0.65rem', fontWeight: 700, padding: '1px 7px',
            borderRadius: 20, background: grp.badgeBg, color: grp.badgeColor,
            textTransform: 'uppercase', letterSpacing: '0.06em',
          }}>
            {km.group ?? 'General'}
          </span>
        </div>
        {/* Current value pill */}
        <div style={{
          fontSize: '0.75rem', fontWeight: 600, whiteSpace: 'nowrap',
          color: grp.color, background: grp.badgeBg,
          padding: '4px 10px', borderRadius: 20,
          border: `1px solid ${grp.border}`,
        }}>
          {setting.dataType === 'user_list'
            ? (() => {
                let ids = [];
                try { ids = JSON.parse(setting.value || '[]'); } catch { /* ignore */ }
                return ids.length === 0 ? 'All CRM users' : `${ids.length} user${ids.length !== 1 ? 's' : ''}`;
              })()
            : setting.value
              ? (setting.dataType === 'decimal' ? `${setting.value}%` : setting.value)
              : 'Not set'}
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {setting.description && (
          <div style={{
            display: 'flex', gap: 8, alignItems: 'flex-start',
            background: '#f8fafc', border: '1px solid #e5e7eb',
            borderRadius: 8, padding: '9px 12px',
          }}>
            <Info size={13} color="#9ca3af" style={{ marginTop: 1, flexShrink: 0 }} />
            <p style={{ margin: 0, fontSize: '0.8rem', color: '#6b7280', lineHeight: 1.6 }}>
              {setting.description}
            </p>
          </div>
        )}

        <div>
          <label style={{
            display: 'block', fontSize: '0.7rem', fontWeight: 700,
            color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6,
          }}>
            New Value
            {km.maxLen && (
              <span style={{ marginLeft: 6, fontWeight: 500, textTransform: 'none', letterSpacing: 0 }}>
                ({draft.length}/{km.maxLen})
              </span>
            )}
          </label>
          {renderInput()}
          {error && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 5, color: '#dc2626', fontSize: '0.78rem' }}>
              <AlertCircle size={12} />
              {error}
            </div>
          )}
        </div>

        {saved && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 7,
            padding: '8px 12px', background: '#f0fdf4',
            border: '1px solid #bbf7d0', borderRadius: 8,
            fontSize: '0.82rem', color: '#15803d', fontWeight: 600,
          }}>
            <CheckCircle2 size={14} />
            Saved successfully.
          </div>
        )}

        <div style={{ display: 'flex', gap: 8 }}>
          {isDirty && (
            <button onClick={handleReset} style={{
              padding: '8px 14px', border: '1.5px solid #e5e7eb',
              borderRadius: 8, background: '#fff', cursor: 'pointer',
              fontSize: '0.83rem', color: '#6b7280', fontWeight: 500,
              display: 'flex', alignItems: 'center', gap: 5,
            }}>
              <RotateCcw size={13} /> Reset
            </button>
          )}
          <button onClick={handleSave} disabled={saving || !isDirty} style={{
            flex: 1, padding: '8px 18px', border: 'none', borderRadius: 8,
            background: (!isDirty || saving) ? '#c4b5fd' : grp.color,
            color: '#fff',
            cursor: (!isDirty || saving) ? 'not-allowed' : 'pointer',
            fontSize: '0.84rem', fontWeight: 600,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            transition: 'background 0.15s',
          }}>
            <Save size={14} />
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function SystemSettingsPage() {
  const [tab,      setTab]      = useState('Sales');
  const [settings, setSettings] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [loadErr,  setLoadErr]  = useState('');

  const fetchSettings = useCallback(async () => {
    setLoading(true); setLoadErr('');
    try {
      const res = await visitsApi.getSystemSettings();
      setSettings(res.data?.data || []);
    } catch (e) {
      setLoadErr(e?.response?.data?.error || e.message || 'Failed to load settings.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const handleSaved = (key, newValue) => {
    setSettings(prev => prev.map(s => s.key === key ? { ...s, value: newValue } : s));
  };

  // Group settings
  const groups = {};
  settings.forEach(s => {
    const g = getGroup(s.key);
    if (!groups[g]) groups[g] = [];
    groups[g].push(s);
  });

  const tabList       = TAB_ORDER.filter(g => groups[g]?.length > 0);
  const activeGroup   = GROUPS[tab] ?? GROUPS.Sales;
  const activeSettings = groups[tab] ?? [];

  return (
    <div className="page-body">
      {/* ── Page header ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: '1.5rem' }}>
        <div style={{
          width: 44, height: 44, borderRadius: 11,
          background: '#f1f5f9', border: '1.5px solid #e2e8f0',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <Settings2 size={22} color="#475569" />
        </div>
        <div>
          <h2 style={{ margin: 0, fontWeight: 800, fontSize: '1.18rem', color: '#0f172a' }}>
            System Settings
          </h2>
          <p style={{ margin: '2px 0 0', fontSize: '0.82rem', color: '#94a3b8' }}>
            Global configuration for all modules — changes apply immediately after saving.
          </p>
        </div>
      </div>

      {/* ── Notice banner ── */}
      <div style={{
        display: 'flex', gap: 10, alignItems: 'flex-start',
        background: '#fefce8', border: '1.5px solid #fde68a',
        borderRadius: 10, padding: '11px 16px', marginBottom: '1.5rem',
      }}>
        <Info size={15} color="#b45309" style={{ marginTop: 1, flexShrink: 0 }} />
        <p style={{ margin: 0, fontSize: '0.82rem', color: '#92400e', lineHeight: 1.6 }}>
          <strong>Queue / visit caps</strong> take effect on the <em>next</em> run — click{' '}
          <em>Run Visit Queue</em> or <em>Run Populate</em> on the respective management page to
          apply new caps immediately.
        </p>
      </div>

      {/* ── Tabs ── */}
      <div style={{ display: 'flex', gap: 4, borderBottom: '2px solid #e5e7eb', marginBottom: '1.5rem' }}>
        {tabList.map(g => {
          const grp   = GROUPS[g];
          const active = tab === g;
          return (
            <button key={g} onClick={() => setTab(g)} style={{
              padding: '9px 18px',
              border: 'none',
              background: active ? grp.bg : 'transparent',
              cursor: 'pointer',
              fontWeight: active ? 700 : 500,
              fontSize: '0.875rem',
              marginBottom: -2,
              borderBottom: `2.5px solid ${active ? grp.color : 'transparent'}`,
              color: active ? grp.color : '#6b7280',
              borderRadius: '8px 8px 0 0',
              display: 'flex', alignItems: 'center', gap: 7,
              transition: 'color 0.15s, background 0.15s',
            }}>
              <grp.Icon size={14} />
              {grp.label}
              <span style={{
                fontSize: '0.68rem', fontWeight: 700, padding: '1px 7px',
                borderRadius: 20,
                background: active ? grp.badgeBg : '#f3f4f6',
                color: active ? grp.badgeColor : '#9ca3af',
              }}>
                {groups[g]?.length ?? 0}
              </span>
            </button>
          );
        })}
      </div>

      {/* ── Group header ── */}
      {!loading && !loadErr && activeSettings.length > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, marginBottom: '1.25rem',
          padding: '12px 16px',
          background: activeGroup.bg, border: `1.5px solid ${activeGroup.border}`,
          borderRadius: 10,
        }}>
          <activeGroup.Icon size={18} color={activeGroup.color} />
          <div>
            <div style={{ fontWeight: 700, fontSize: '0.9rem', color: activeGroup.color }}>
              {activeGroup.label}
            </div>
            <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: 1 }}>
              {activeGroup.description}
            </div>
          </div>
        </div>
      )}

      {/* ── Content ── */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '4rem', color: '#94a3b8', fontSize: '0.9rem' }}>
          Loading settings…
        </div>
      )}
      {loadErr && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '12px 16px', background: '#fef2f2',
          border: '1.5px solid #fecaca', borderRadius: 10,
          color: '#dc2626', fontSize: '0.85rem',
        }}>
          <AlertCircle size={15} /> {loadErr}
        </div>
      )}
      {!loading && !loadErr && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
          gap: '1rem',
        }}>
          {activeSettings.map(s => (
            <SettingCard key={s.key} setting={s} onSaved={handleSaved} />
          ))}
          {activeSettings.length === 0 && (
            <div style={{ color: '#9ca3af', fontSize: '0.88rem', padding: '1rem 0' }}>
              No settings in this group yet. Run <code>npm run sync-settings</code> to seed them.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
